import { action, computed, makeObservable, observable, reaction } from 'mobx'
import Workspace from 'iam/models/Workspace'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import {
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { CursorPageQuery, Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { Bundle } from './bundle'
import { BundleStore } from './BundleStore'
import BundleRequestQueryFilterBuilder, {
  BundleArchivedFilterOptions,
} from './BundleRequestQueryFilterBuilder'
import WorkspaceSearchViewModel from './WorkspaceSearchViewModel'

export type RefreshBundles = (workspace?: Workspace) => Promise<void>

export type BundleFilter = {
  workspace: Workspace | undefined
}

export default class BundleSearchViewModel {
  /**
   * The current search filter based on selected tab.
   */
  @observable currentArchivedStatusFilter = BundleArchivedFilterOptions.ACTIVE

  /**
   * Workspace filters used for handling bundles
   */
  filters = new FiltersViewModel<BundleFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspace: {
        icon: 'pending',
        title: 'View:',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            undefined,
            // The workspace filter options are managed externally from the
            // filter view model.
            FilterItemSource.static([]),
            false,
          ),
      },
    },
  })

  @computed
  get canManageBundles() {
    return this.getWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS'),
    )
  }

  constructor(
    private bundleStore: BundleStore,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private bundleWorkspaceSearcher: WorkspaceSearchViewModel,
    private filterStorage?: FilterStorage,
  ) {
    makeObservable(this)

    reaction(
      () => this.filters.appliedFilters.workspace,
      () => {
        this.refreshItems()
      },
    )

    reaction(
      () => this.currentArchivedStatusFilter,
      () => {
        this.refreshItems()
      },
    )
  }

  /**
   * This includes all bundles from all workspaces that the user has permission in.
   */
  @computed
  get allBundles() {
    return this.bundleStore.allBundles()
  }

  /**
   * Workspace filter
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace | undefined
    >
  }

  /**
   * Search text computed getter.
   */
  @computed
  get workspace() {
    return this.workspaceFilter.value
  }

  /**
   * All Workspaces ids that have bundles.
   */
  @computed
  get allWorkspaceIdsWithBundles() {
    return this.bundleWorkspaceSearcher.activeWorkspaces.map(
      (workspaceWithBundle) => workspaceWithBundle.workspace.id,
    )
  }

  @action.bound
  setCurrentArchivedStatusFilter(filter: BundleArchivedFilterOptions) {
    this.currentArchivedStatusFilter = filter
  }

  @action.bound
  setWorkspace(value?: Workspace) {
    this.workspaceFilter.change(value)
  }

  private async _fetchBundles(
    workspaceId: Id,
    limit: number,
    pageToken?: string,
    orderBy: string = 'name',
  ) {
    return await this.bundleStore.fetchBundles(
      workspaceId,
      limit,
      pageToken,
      new BundleRequestQueryFilterBuilder()
        .withArchivedStatus(this.currentArchivedStatusFilter)
        .build(),
      orderBy,
    )
  }

  scrollProvider = new InfiniteScrollProvider<Bundle>({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging) => this._fetchAllBundles(this.workspace?.id, paging),
  })

  private async _fetchAllBundles(workspaceId?: Id, paging?: CursorPageQuery) {
    const limit = paging?.limit ?? 200

    if (workspaceId) {
      const fetchWorkspaceBundles = await this._fetchBundles(
        workspaceId,
        limit,
        paging?.after ?? undefined,
        'archived_items_count DESC',
      )
      return {
        data: fetchWorkspaceBundles.data,
        cursor: fetchWorkspaceBundles.cursor,
      }
    }

    if (!this.canManageBundles) {
      return {
        data: [],
        cursor: '',
      }
    }

    let allBundles: Bundle[] = []
    let lastCursor: string | null = paging?.after || null

    for (const workspaceId of this.allWorkspaceIdsWithBundles) {
      // If we've already fetched enough bundles according to the limit, break.
      if (allBundles.length >= limit) {
        break
      }

      // If a cursor exists and belongs to another workspace, skip.
      if (lastCursor && !lastCursor.startsWith(Id.assert(workspaceId))) {
        continue
      }

      // Extract the actual cursor for the workspace from the combined cursor.
      const actualCursor = extractCursorAfterUnderscore(lastCursor)

      const bundles = await this._fetchBundles(workspaceId, limit, actualCursor)
      allBundles = allBundles.concat(bundles.data)

      // Prepare the cursor for the next iteration.
      lastCursor = bundles.data.length
        ? `${workspaceId}_${bundles.cursor}`
        : null
    }

    return {
      data: allBundles,
      cursor: lastCursor,
    }
  }

  async refreshItems() {
    this.bundleStore.bundles.clear()
    this.scrollProvider.reset(false)
    this.scrollProvider.fetch()
  }

  async activate() {
    await this.workspaceFilter.activate()

    this.scrollProvider.fetch()
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.filters.deactivate()
  }
}

function extractCursorAfterUnderscore(inputString: string | null) {
  if (inputString === null) {
    return undefined
  }

  const parts = inputString.split('_')

  return parts.length > 1 ? parts[1] : undefined
}
